import { useState, useEffect, type ReactNode } from 'react';
import PermissionMatrix from './PermissionMatrix';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface RoleFormModalProps {
  role?: any;
  allPermissions: any;
  onSubmit: (form: Record<string, unknown>) => Promise<void> | void;
  onCancel: () => void;
  /**
   * Optional group-access editor (local node only). Given the selected group ids and an
   * onChange, returns the picker UI. When provided, the form also submits `group_ids`.
   * Cloud omits this — its roles have no group access.
   */
  renderGroupAccess?: (selectedIds: string[], onChange: (ids: string[]) => void) => ReactNode;
  /**
   * View-only: the current user may not edit this role (e.g. an admin opening the
   * super_admin role). Disables every field and hides the submit button. Distinct
   * from `role.is_system` — default roles are now editable, so only the role NAME
   * stays locked for system roles (renaming would break the RBAC bypass).
   */
  readOnly?: boolean;
}

/**
 * Create/edit a role. Permission selection is shared; group access is an optional slot
 * so the same modal serves the local node (with groups) and cloud (without).
 */
export default function RoleFormModal({ role, allPermissions, onSubmit, onCancel, renderGroupAccess, readOnly = false }: RoleFormModalProps) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    permission_ids: [] as string[],
    group_ids: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (role) {
      setForm({
        name: role.name || '',
        description: role.description || '',
        permission_ids: role.permissions?.map((p: any) => p.id) || [],
        group_ids: role.group_access?.map((g: any) => g.id) || [],
      });
    } else {
      setForm({ name: '', description: '', permission_ids: [], group_ids: [] });
    }
  }, [role]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('Role name is required');
      return;
    }
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        description: form.description,
        permission_ids: form.permission_ids,
      };
      if (renderGroupAccess) payload.group_ids = form.group_ids;
      await onSubmit(payload);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save role');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 overlay-backdrop" onClick={onCancel} />

      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-card shadow-modal mx-4">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <h2 className="text-[16px] font-semibold text-foreground">
            {role ? 'Edit Role' : 'Create New Role'}
          </h2>
          <button
            onClick={onCancel}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-[13px] text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-[13px] font-medium text-foreground mb-1.5">Role Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={role?.is_system || readOnly}
                placeholder="e.g. supervisor"
                className="w-full rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {role?.is_system && (
                <p className="mt-1 text-[11px] text-muted-foreground">System role names cannot be changed.</p>
              )}
            </div>
            <div>
              <label className="block text-[13px] font-medium text-foreground mb-1.5">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={readOnly}
                placeholder="Short description of this role"
                className="w-full rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-foreground mb-3">Permissions</label>
            {readOnly && (
              <p className="mb-2 text-[11px] text-muted-foreground">
                You do not have permission to edit this role — viewing only.
              </p>
            )}
            <PermissionMatrix
              allPermissions={allPermissions}
              selectedIds={form.permission_ids}
              onChange={(ids) => setForm({ ...form, permission_ids: ids })}
              disabled={readOnly}
            />
          </div>

          {renderGroupAccess && (
            <div>{renderGroupAccess(form.group_ids, (ids) => setForm({ ...form, group_ids: ids }))}</div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted transition-colors"
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button
                type="submit"
                disabled={loading}
                className="rounded-lg bg-brand px-5 py-2 text-[13px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50 transition-colors"
              >
                {loading ? 'Saving…' : role ? 'Update Role' : 'Create Role'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
